import React, { useEffect, useState, useCallback } from 'react';
import Sidebar from '../Includes/Sidebar';
import Footer from '../Includes/Footer';
import { Link, useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import GeneralService from '../../services/general.service';

export default function AppointmentList() {
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState('all');
    const [serviceFilter, setServiceFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [loadingStates, setLoadingStates] = useState({}); // Track loading state for each appointment

    // API Call to Fetch Appointments Data
    const fetchAppointments = async () => {
        try {
            setIsLoading(true);
            const response = await GeneralService.getAppointments();
            console.log("API Response:", response.data);

            if (Array.isArray(response.data)) {
                const appointmentsWithIds = response.data.map((appointment, index) => {
                    if (!appointment.id) {
                        console.warn(`Appointment at index ${index} has no ID, using index as fallback:`, appointment);
                        return { ...appointment, id: appointment.appointment_id || index + 1 };
                    }
                    return appointment;
                });
                setAppointments(appointmentsWithIds);
            } else if (response.data && response.data.appointments) {
                setAppointments(response.data.appointments);
            } else if (response.data && response.data.data && response.data.data.appointments) {
                setAppointments(response.data.data.appointments);
            } else {
                console.error("Unexpected response format:", response.data);
                setAppointments([]);
            }
        } catch (error) {
            console.error("Error fetching appointments:", error);
            console.error("Error details:", error.response?.data);
            setAppointments([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle delete function
    const handleDelete = async (appointmentId) => {
        if (!appointmentId) {
            alert("Appointment ID is missing. Cannot delete.");
            return;
        }

        try {
            await GeneralService.deleteAppointment(appointmentId);
            fetchAppointments();
        } catch (error) {
            console.error("Error deleting appointment:", error);
            alert("Failed to delete appointment. Please try again.");
        }
    };

    // Handle status update with loading state
    const handleStatusUpdate = async (appointmentId, newStatus) => {
        if (!appointmentId) {
            alert("Appointment ID is missing. Cannot update status.");
            return;
        }

        // Set loading state for this specific appointment
        setLoadingStates(prev => ({ ...prev, [appointmentId]: true }));

        try {
            await GeneralService.updateAppointmentStatus(appointmentId, newStatus);
            
            // Update the appointment status locally for instant feedback
            setAppointments(prev => 
                prev.map(appointment => 
                    appointment.id === appointmentId 
                        ? { ...appointment, status: newStatus }
                        : appointment
                )
            );
            
            // Optional: You can also refetch all appointments if needed
            // fetchAppointments();
            
        } catch (error) {
            console.error("Error updating appointment status:", error);
            alert("Failed to update appointment status. Please try again.");
        } finally {
            // Remove loading state
            setLoadingStates(prev => {
                const newState = { ...prev };
                delete newState[appointmentId];
                return newState;
            });
        }
    };

    useEffect(() => {
        fetchAppointments();
    }, []);

    const handleSearch = useCallback(
        debounce((query) => {
            setSearchQuery(query);
            setCurrentPage(1);
        }, 300),
        []
    );

    useEffect(() => {
        return () => handleSearch.cancel();
    }, [handleSearch]);

    // Filter appointments based on search query and filters
    const filteredAppointments = appointments.filter(appointment => 
        (appointment.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         appointment.doctor?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         appointment.service?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         appointment.phone?.includes(searchQuery)) &&
        (statusFilter === 'all' || appointment.status === statusFilter) &&
        (serviceFilter === 'all' || appointment.service?.toLowerCase().includes(serviceFilter.toLowerCase())) &&
        (dateFilter === '' || appointment.date === dateFilter)
    );

    const totalRecords = filteredAppointments.length;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const startIndex = (currentPage - 1) * recordsPerPage;
    const displayedAppointments = filteredAppointments.slice(startIndex, startIndex + recordsPerPage);

    // Format date function
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get unique services for filter
    const uniqueServices = [...new Set(appointments.map(appointment => appointment.service).filter(Boolean))];

    // Get status badge class
    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'confirmed': return 'bg-success';
            case 'pending': return 'bg-warning';
            case 'cancelled': return 'bg-danger';
            case 'completed': return 'bg-info';
            default: return 'bg-secondary';
        }
    };

    // Get available actions based on current status
    const getAvailableActions = (currentStatus) => {
        const status = currentStatus?.toLowerCase();
        const actions = [];

        switch (status) {
            case 'pending':
                actions.push(
                    { status: 'confirmed', label: 'Confirm', icon: 'fas fa-check', class: 'btn-success' },
                    { status: 'cancelled', label: 'Cancel', icon: 'fas fa-times', class: 'btn-danger' }
                );
                break;
            case 'confirmed':
                actions.push(
                    { status: 'completed', label: 'Complete', icon: 'fas fa-check-double', class: 'btn-info' },
                    { status: 'cancelled', label: 'Cancel', icon: 'fas fa-times', class: 'btn-danger' }
                );
                break;
            case 'completed':
                // No actions available for completed appointments
                break;
            case 'cancelled':
                actions.push(
                    { status: 'pending', label: 'Reopen', icon: 'fas fa-undo', class: 'btn-warning' }
                );
                break;
            default:
                actions.push(
                    { status: 'confirmed', label: 'Confirm', icon: 'fas fa-check', class: 'btn-success' },
                    { status: 'cancelled', label: 'Cancel', icon: 'fas fa-times', class: 'btn-danger' }
                );
        }

        return actions;
    };

    return (
        <div className="content">
            <Sidebar />
            <div className="container-fluid pt-4 px-4">
                <div className="bg-light text-center rounded p-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h5 className="mb-0">
                            Total Appointments <span className="number-records">({totalRecords})</span>
                        </h5>
                        <div className="d-flex gap-2">
                            <select 
                                className="form-control" 
                                value={statusFilter}
                                onChange={(e) => { 
                                    setStatusFilter(e.target.value); 
                                    setCurrentPage(1); 
                                }}
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="completed">Completed</option>
                            </select>
                            <select 
                                className="form-control" 
                                value={serviceFilter}
                                onChange={(e) => { 
                                    setServiceFilter(e.target.value); 
                                    setCurrentPage(1); 
                                }}
                            >
                                <option value="all">All Services</option>
                                {uniqueServices.map(service => (
                                    <option key={service} value={service}>{service}</option>
                                ))}
                            </select>
                            <input 
                                type="date" 
                                className="form-control"
                                value={dateFilter}
                                onChange={(e) => { 
                                    setDateFilter(e.target.value); 
                                    setCurrentPage(1); 
                                }}
                                placeholder="Filter by date"
                            />
                            <input 
                                type="text" 
                                placeholder="Search appointments..." 
                                className="form-control" 
                                onChange={(e) => handleSearch(e.target.value)} 
                            />
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead className="bg-primary">
                                <tr className="text-white">
                                    <th>No</th>
                                    <th>Patient Name</th>
                                    <th>Phone</th>
                                    <th>Service</th>
                                    <th>Doctor</th>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Status</th>
                                    <th>Created At</th>
                                    <th>Quick Actions</th>
                                    {/* <th>Delete</th> */}
                                </tr>
                            </thead>

                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="11" className="text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : displayedAppointments.length > 0 ? (
                                    displayedAppointments.map((appointment, index) => (
                                        <tr key={appointment.id || index}>
                                            <td>{startIndex + index + 1}</td>
                                            <td>{appointment.name || 'N/A'}</td>
                                            <td>
                                                <a href={`tel:${appointment.phone}`} className="text-decoration-none">
                                                    {appointment.phone || 'N/A'}
                                                </a>
                                            </td>
                                            <td>{appointment.service || 'N/A'}</td>
                                            <td>{appointment.doctor || 'N/A'}</td>
                                            <td>{appointment.date || 'N/A'}</td>
                                            <td>{appointment.time || 'N/A'}</td>
                                            <td>
                                                <span className={`badge ${getStatusBadgeClass(appointment.status)}`}>
                                                    {appointment.status || 'Pending'}
                                                </span>
                                            </td>
                                            <td>{formatDate(appointment.created_at)}</td>
                                            <td>
                                                {appointment.status?.toLowerCase() === 'confirmed' ? (
                                                    // For completed appointments, show a subtle completed indicator
                                                    <div className="d-flex justify-content-center">
                                                        <small className="text-success fw-bold">
                                                            <i className="fas fa-check-circle me-1"></i>
                                                            Completed
                                                        </small>
                                                    </div>
                                                ) : (
                                                    // For all other statuses, show action buttons
                                                    <div className="d-flex gap-1 flex-wrap">
                                                        {loadingStates[appointment.id] ? (
                                                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                        ) : (
                                                            getAvailableActions(appointment.status).map((action, actionIndex) => (
                                                                <button
                                                                    key={actionIndex}
                                                                    className={`btn btn-sm ${action.class}`}
                                                                    title={action.label}
                                                                    onClick={() => handleStatusUpdate(appointment.id, action.status)}
                                                                    disabled={loadingStates[appointment.id]}
                                                                >
                                                                    <i className={`${action.icon} me-1`}></i>
                                                                    {action.label}
                                                                </button>
                                                            ))
                                                        )}
                                                        {getAvailableActions(appointment.status).length === 0 && 
                                                         appointment.status?.toLowerCase() !== 'completed' && (
                                                            <small className="text-muted">No actions available</small>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            {/* <td>
                                                <button 
                                                    className="btn btn-sm btn-danger"
                                                    title="Delete"
                                                    onClick={() => {
                                                        if (window.confirm('Are you sure you want to delete this appointment?')) {
                                                            handleDelete(appointment.id);
                                                        }
                                                    }}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </td> */}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="11" className="text-center">No appointments found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!isLoading && totalPages > 1 && (
                        <div className="pagination-container mt-3">
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setCurrentPage(currentPage - 1)} 
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                </li>
                                {[...Array(totalPages).keys()].map((num) => (
                                    <li 
                                        key={num + 1} 
                                        className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}
                                    >
                                        <button 
                                            className="page-link" 
                                            onClick={() => setCurrentPage(num + 1)}
                                        >
                                            {num + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setCurrentPage(currentPage + 1)} 
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}